import React from "react";
import Layout from "../components/Layout/Layout";
import { useNavigate } from "react-router-dom";
import {
  FiTruck,
  FiRefreshCw,
  FiVideo,
  FiCreditCard,
  FiMail,
} from "react-icons/fi";

const ShippingReturns = () => {
  const navigate = useNavigate();

  const PALETTE = {
    DARK_NAVY: "#1C2E4A",
    ACCENT_BROWN: "#7C4F2B",
    LIGHT_CREAM: "#FAF8F5",
  };

  return (
    <Layout title={"Russh Berry | Shipping, Returns & Exchanges Policy"}>
      <div
        className="min-h-screen pt-24 pb-20 transition-colors duration-500"
        style={{ backgroundColor: PALETTE.LIGHT_CREAM }}
      >
        {/* WIDER CONTAINER: Upgraded to max-w-7xl and tighter padding */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12">
          {/* HEADER SECTION */}
          <div className="text-center max-w-3xl mx-auto mb-16 sm:mb-20">
            <p
              className="text-xs sm:text-sm font-semibold tracking-[0.3em] uppercase mb-3"
              style={{ color: PALETTE.ACCENT_BROWN }}
            >
              Russh Berry Policies
            </p>
            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mb-6 leading-tight"
              style={{ color: PALETTE.DARK_NAVY }}
            >
              Shipping, Returns &amp; Exchanges
            </h1>
            <div
              className="w-16 h-[1px] mx-auto mb-6"
              style={{ backgroundColor: PALETTE.ACCENT_BROWN }}
            />
            <p className="text-gray-600 text-sm sm:text-base font-light leading-relaxed">
              Everything you need to know about our dispatch timelines,
              eligibility criteria, and seamless return experience.
            </p>
          </div>

          {/* 2-COLUMN GRID ON DESKTOP TO FILL HORIZONTAL SPACE */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10 items-stretch">
            {/* 1. SHIPPING POLICY */}
            <div
              className="bg-white p-8 sm:p-10 lg:p-12 shadow-md border border-gray-100/80 transition-all duration-300 hover:shadow-xl flex flex-col justify-between"
              style={{ borderLeft: `4px solid ${PALETTE.DARK_NAVY}` }}
            >
              <div className="flex items-start gap-6">
                <div
                  className="w-12 h-12 rounded-full bg-[#FAF8F5] flex items-center justify-center shrink-0 mt-1"
                  style={{ color: PALETTE.ACCENT_BROWN }}
                >
                  <FiTruck size={22} />
                </div>
                <div>
                  <h2
                    className="text-xl sm:text-2xl font-light tracking-wider uppercase mb-4"
                    style={{ color: PALETTE.DARK_NAVY }}
                  >
                    Shipping &amp; Delivery
                  </h2>
                  <p className="text-gray-700 font-light text-sm sm:text-base leading-relaxed">
                    Russh Berry currently ships exclusively within India. Orders
                    are typically processed within 1–2 business days and are
                    generally delivered within 6–7 business days from the date
                    of dispatch. Once your order is shipped, tracking details
                    will be shared via email. Delivery timelines may vary due to
                    public holidays, peak seasons, or circumstances beyond our
                    control.
                  </p>
                </div>
              </div>
            </div>

            {/* 2. RETURNS & EXCHANGES ELIGIBILITY */}
            <div
              className="bg-white p-8 sm:p-10 lg:p-12 shadow-md border border-gray-100/80 transition-all duration-300 hover:shadow-xl flex flex-col justify-between"
              style={{ borderLeft: `4px solid ${PALETTE.DARK_NAVY}` }}
            >
              <div className="flex items-start gap-6">
                <div
                  className="w-12 h-12 rounded-full bg-[#FAF8F5] flex items-center justify-center shrink-0 mt-1"
                  style={{ color: PALETTE.ACCENT_BROWN }}
                >
                  <FiRefreshCw size={22} />
                </div>
                <div>
                  <h2
                    className="text-xl sm:text-2xl font-light tracking-wider uppercase mb-4"
                    style={{ color: PALETTE.DARK_NAVY }}
                  >
                    Returns &amp; Exchanges
                  </h2>
                  <p className="text-gray-700 font-light text-sm sm:text-base leading-relaxed">
                    We want you to be delighted with your purchase. If you
                    receive a defective, damaged, or incorrect item, please
                    contact our Customer Care team within 7 days of delivery to
                    initiate a return or exchange. To be eligible, the product
                    must be unused, unwashed, and returned with all original
                    tags and packaging intact.
                  </p>
                </div>
              </div>
            </div>

            {/* 3. MANDATORY UNBOXING VIDEO (HIGHLIGHTED BLOCK) */}
            <div
              className="bg-[#FAF8F5] p-8 sm:p-10 lg:p-12 shadow-md border-2 transition-all duration-300 hover:shadow-xl relative overflow-hidden flex flex-col justify-between"
              style={{
                borderColor: PALETTE.ACCENT_BROWN,
                borderLeft: `4px solid ${PALETTE.ACCENT_BROWN}`,
              }}
            >
              <div className="flex items-start gap-6">
                <div
                  className="w-12 h-12 rounded-full bg-white flex items-center justify-center shrink-0 mt-1 shadow-sm"
                  style={{ color: PALETTE.ACCENT_BROWN }}
                >
                  <FiVideo size={22} />
                </div>
                <div>
                  <span
                    className="inline-block text-[11px] font-semibold tracking-[0.2em] uppercase px-3 py-1 rounded-full bg-white mb-3 shadow-2xs"
                    style={{ color: PALETTE.ACCENT_BROWN }}
                  >
                    Mandatory Requirement
                  </span>
                  <h2
                    className="text-xl sm:text-2xl font-light tracking-wider uppercase mb-4"
                    style={{ color: PALETTE.DARK_NAVY }}
                  >
                    Unboxing Video Policy
                  </h2>
                  <p className="text-gray-800 font-normal text-sm sm:text-base leading-relaxed">
                    For all claims involving damage, defects, or incorrect
                    items, an uninterrupted unboxing video clearly showing the
                    sealed package being opened and the condition of the product
                    is mandatory. Requests submitted without a valid unboxing
                    video may not qualify for a return, exchange, or refund.
                  </p>
                </div>
              </div>
            </div>

            {/* 4. REFUNDS & INSPECTION */}
            <div
              className="bg-white p-8 sm:p-10 lg:p-12 shadow-md border border-gray-100/80 transition-all duration-300 hover:shadow-xl flex flex-col justify-between"
              style={{ borderLeft: `4px solid ${PALETTE.DARK_NAVY}` }}
            >
              <div className="flex items-start gap-6">
                <div
                  className="w-12 h-12 rounded-full bg-[#FAF8F5] flex items-center justify-center shrink-0 mt-1"
                  style={{ color: PALETTE.ACCENT_BROWN }}
                >
                  <FiCreditCard size={22} />
                </div>
                <div>
                  <h2
                    className="text-xl sm:text-2xl font-light tracking-wider uppercase mb-4"
                    style={{ color: PALETTE.DARK_NAVY }}
                  >
                    Refunds &amp; Inspection
                  </h2>
                  <p className="text-gray-700 font-light text-sm sm:text-base leading-relaxed">
                    Once the returned item is received and passes inspection,
                    approved refunds will be processed to the original payment
                    method within 5–7 business days. Russh Berry reserves the
                    right to decline returns or exchanges that do not meet the
                    above conditions.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* FOOTER ASSISTANCE SECTION (STRETCHES FULL WIDTH OF 7XL) */}
          <div className="mt-16 sm:mt-20 text-center bg-white p-8 sm:p-12 border border-gray-200/60 shadow-sm w-full">
            <h3
              className="text-lg sm:text-xl font-light tracking-widest uppercase mb-3"
              style={{ color: PALETTE.DARK_NAVY }}
            >
              Need Further Assistance?
            </h3>
            <p className="text-gray-500 font-light text-sm mb-6 max-w-md mx-auto">
              Our Customer Care team is at your disposal to assist with any
              queries regarding your order or our policies.
            </p>
            <button
              onClick={() => navigate("/contact")}
              className="inline-flex items-center gap-3 px-8 py-3.5 text-xs font-semibold tracking-[0.25em] uppercase text-white transition-all duration-300 shadow-md hover:shadow-xl"
              style={{ backgroundColor: PALETTE.DARK_NAVY }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = PALETTE.ACCENT_BROWN;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = PALETTE.DARK_NAVY;
              }}
            >
              <FiMail className="text-sm" />
              <span>Contact Customer Care</span>
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ShippingReturns;
